import React, { useContext, useEffect, useRef, useState } from 'react';
import L, { LatLng, geoJson } from 'leaflet';
import { Feature, FeatureCollection } from 'geojson';
import { JSDCContext } from '../../../JSDC/Context';
import { fetchAndConvertGeoJSON } from '../../../utils/coordinateTransform';
import './LayerItem.scss';

const BASE_URL = 'https://mapelon.com/static/twrna/vector-layer/';

export const STRUCTURE_LAYERS = [
  {
    id: '00',
    file: 'guardian_map',
    name: '河溪守護者地圖',
    color: '#febf00',
    icon: 'la-life-ring',
    sourceCrs: 'WGS84',
  },
  {
    id: '01',
    file: '01_transverse_bed_stabilization',
    name: '橫向構造物_固床工',
    color: '#D32F2F',
    icon: 'la-wave-square',
  },
  {
    id: '02',
    file: '02_transverse_dam',
    name: '橫向構造物_壩',
    color: '#F57C00',
    icon: 'la-wave-square',
  },
  {
    id: '03',
    file: '03_transverse_weir',
    name: '橫向構造物_堰',
    color: '#FBC02D',
    icon: 'la-wave-square',
  },
  {
    id: '04',
    file: '04_transverse_other',
    name: '橫向構造物_其他',
    color: '#795548',
    icon: 'la-wave-square',
  },
  {
    id: '05',
    file: '05_longitudinal_revetment',
    name: '縱向構造物_護岸',
    color: '#388E3C',
    icon: 'la-wave-square',
  },
  {
    id: '06',
    file: '06_longitudinal_levee',
    name: '縱向構造物_堤防',
    color: '#1976D2',
    icon: 'la-wave-square',
  },
  {
    id: '07',
    file: '07_longitudinal_covered',
    name: '縱向構造物_加蓋',
    color: '#7B1FA2',
    icon: 'la-wave-square',
  },
  {
    id: '08',
    file: '08_longitudinal_other',
    name: '縱向構造物_其他',
    color: '#000000',
    icon: 'la-wave-square',
  },
] as const;

type StructureCfg = (typeof STRUCTURE_LAYERS)[number];

interface CachedEntry {
  geojson: FeatureCollection;
  allPoints: boolean;
  pointLatLngs: LatLng[];
  active?: L.Layer;
}

const extractPointLatLngs = (geojson: FeatureCollection): LatLng[] => {
  const out: LatLng[] = [];
  for (const f of geojson.features) {
    if (f.geometry?.type === 'Point') {
      const [lng, lat] = f.geometry.coordinates;
      out.push(new LatLng(lat, lng));
    }
  }
  return out;
};

const StructureLayers = () => {
  const {
    Jsdc,
    setOpenDialog,
    setFeatureProperties,
    setFeatureColor,
    setFeatureIcon,
    toolSetting,
    heatMapControll,
  } = useContext(JSDCContext);
  const [openMap, setOpenMap] = useState<Record<string, boolean>>({});
  const cacheRef = useRef<Record<string, CachedEntry>>({});

  const handleFeatureClick = (feature: Feature, color: string, icon: string) => {
    if (!feature.properties) return;
    setFeatureProperties(feature.properties);
    setFeatureColor(color);
    setFeatureIcon(icon);
    setOpenDialog(true);
  };

  const shouldHideMarkers = () => toolSetting.hotspot && !toolSetting.cluster;

  const buildRepresentation = (
    entry: CachedEntry,
    color: string,
    iconClass: string,
    useCluster: boolean,
  ): L.Layer => {
    const hidden = shouldHideMarkers();
    const vector = geoJson(entry.geojson, {
      style: () => ({ color, weight: 10 }),
      pointToLayer: (_feature, latlng) => {
        const icon = L.divIcon({
          html: `<div class="cat-icon" style="background:${color}"><i class="las ${iconClass}"></i></div>`,
          className: 'cat-icon-wrap',
          iconSize: [32, 32],
          iconAnchor: [16, 16],
        });
        const marker = L.marker(latlng, { icon });
        if (hidden) marker.setOpacity(0);
        return marker;
      },
    });
    vector.on('click', (e) => handleFeatureClick(e.sourceTarget.feature, color, iconClass));

    if (!entry.allPoints || !useCluster) return vector;

    const cluster = L.markerClusterGroup({
      showCoverageOnHover: false,
      chunkedLoading: true,
      disableClusteringAtZoom: 17,
    });
    cluster.addLayer(vector);
    return cluster;
  };

  const mount = (cfg: StructureCfg) => {
    const map = Jsdc.viewer;
    const entry = cacheRef.current[cfg.id];
    if (!map || !entry) return;

    const layer = buildRepresentation(entry, cfg.color, cfg.icon, toolSetting.cluster);
    entry.active = layer;
    map.addLayer(layer);
    if ('getBounds' in layer) {
      const bounds = (layer as L.GeoJSON).getBounds();
      if (bounds.isValid()) map.fitBounds(bounds);
    }
  };

  const unmount = (cfg: StructureCfg) => {
    const map = Jsdc.viewer;
    const entry = cacheRef.current[cfg.id];
    if (!map || !entry?.active) return;
    map.removeLayer(entry.active);
    entry.active = undefined;
  };

  const toggleLayer = async (cfg: StructureCfg, next: boolean) => {
    setOpenMap((m) => ({ ...m, [cfg.id]: next }));
    if (next) {
      if (!cacheRef.current[cfg.id]) {
        const geojson = await fetchAndConvertGeoJSON(
          BASE_URL + cfg.file + '.geojson',
          cfg.file,
          'sourceCrs' in cfg ? cfg.sourceCrs : 'TWD97',
        );
        cacheRef.current[cfg.id] = {
          geojson,
          allPoints: geojson.features.every((f) => !f.geometry || f.geometry.type === 'Point'),
          pointLatLngs: extractPointLatLngs(geojson),
        };
      }
      mount(cfg);
    } else {
      unmount(cfg);
    }
  };

  // Cluster 開關變動 → 對每個已開啟的 Point 層重建 representation
  useEffect(() => {
    for (const cfg of STRUCTURE_LAYERS) {
      const entry = cacheRef.current[cfg.id];
      if (!openMap[cfg.id] || !entry?.allPoints) continue;
      unmount(cfg);
      mount(cfg);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toolSetting.cluster]);

  // Hotspot 或開啟狀態變動 → 推/撤 Point latlngs 到共用 heatmap
  useEffect(() => {
    for (const cfg of STRUCTURE_LAYERS) {
      const entry = cacheRef.current[cfg.id];
      const active = toolSetting.hotspot && openMap[cfg.id] && entry?.pointLatLngs.length;
      heatMapControll.attachLatLngs(cfg.id, active ? entry!.pointLatLngs : []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toolSetting.hotspot, openMap]);

  // Hotspot + cluster OFF → 隱藏個別 marker（與 trails 行為一致）
  useEffect(() => {
    const opacity = shouldHideMarkers() ? 0 : 1;
    for (const cfg of STRUCTURE_LAYERS) {
      const entry = cacheRef.current[cfg.id];
      if (!entry?.active) continue;
      (entry.active as L.LayerGroup).eachLayer((sub) => {
        if (sub instanceof L.Marker) sub.setOpacity(opacity);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toolSetting.hotspot, toolSetting.cluster]);

  return (
    <>
      {STRUCTURE_LAYERS.map((cfg) => (
        <div className="dui-LayerItem" key={cfg.id}>
          <input
            type="checkbox"
            checked={!!openMap[cfg.id]}
            onChange={(e) => toggleLayer(cfg, e.currentTarget.checked)}
          />
          <div className="dui-LayerItem-content">
            <span>{cfg.name}</span>
          </div>
        </div>
      ))}
    </>
  );
};

export default StructureLayers;
